import React, { useEffect, useState } from 'react';
import { inventoryApi, farmApi, masterDataApi, API_BASE_URL } from '../services/api';
import { FeedInventory, Farm } from '../types';
import { handleApiError, showError, showSuccess } from '../utils/handleApiError';
import ConfirmModal from '../components/ConfirmModal';
import MasterDataCombobox from '../components/MasterDataCombobox';

const Inventory: React.FC = () => {
  const [feeds, setFeeds] = useState<FeedInventory[]>([]);
  const [medicines, setMedicines] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [medicineTransactions, setMedicineTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [farms, setFarms] = useState<Farm[]>([]);
  const [selectedFarmId, setSelectedFarmId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState('inventory');
  const [showAddFeedModal, setShowAddFeedModal] = useState(false);
  const [showEditTransactionModal, setShowEditTransactionModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<any>(null);
  const [showEditMedicineTransactionModal, setShowEditMedicineTransactionModal] = useState(false);
  const [editingMedicineTransaction, setEditingMedicineTransaction] = useState<any>(null);
  const [showAddMedicineModal, setShowAddMedicineModal] = useState(false);
  const [availableFeeds, setAvailableFeeds] = useState<any[]>([]);
  const [availableMedicines, setAvailableMedicines] = useState<any[]>([]);
  const [feedQuantityUnits, setFeedQuantityUnits] = useState<any[]>([]);
  const [medicineQuantityUnits, setMedicineQuantityUnits] = useState<any[]>([]);
  const [confirmAction, setConfirmAction] = useState<{ action: 'deleteFeed' | 'deleteMedicine'; id: number } | null>(null);
  const [newFeed, setNewFeed] = useState({
    feed_id: '',
    quantity: '',
    quantity_unit: '',
    unit_cost: '',
    storage_location: '',
    transaction_date: new Date().toISOString().split('T')[0],
    // Cost related fields
    invoice_number: '',
    total_cost: '',
    cost_notes: ''
  });
  const [newMedicine, setNewMedicine] = useState({
    medicine_id: '',
    quantity: '',
    quantity_unit: '',
    expiry_date: '',
    batch_number: '',
    unit_cost: '',
    invoice_number: '',
    total_cost: ''
  });

  // Load farms on mount
  useEffect(() => {
    const loadFarms = async () => {
      try {
        const farmsData = await farmApi.getAll();
        setFarms(farmsData);
        if (farmsData.length > 0 && !selectedFarmId) {
          setSelectedFarmId(farmsData[0].id);
        }
      } catch (error) {
        console.error('Error loading farms:', error);
        handleApiError(error, 'Hiba a telephelyek betöltésekor');
      }
    };
    loadFarms();
    loadMasterData();
  }, []);

  // Reload data when farm or tab changes
  useEffect(() => {
    if (selectedFarmId) {
      loadInventory();
      if (activeTab === 'transactions') {
        loadTransactions();
        loadMedicineTransactions();
      }
    }
  }, [selectedFarmId, activeTab]);

  const loadInventory = async () => {
    if (!selectedFarmId) return;
    try {
      setLoading(true);
      const [feedsResponse, medicinesResponse] = await Promise.all([
        inventoryApi.getFeeds(selectedFarmId),
        inventoryApi.getMedicines(selectedFarmId)
      ]);

      setFeeds(feedsResponse);
      setMedicines(medicinesResponse);
    } catch (error) {
      console.error('Error loading inventory:', error);
      handleApiError(error, 'Hiba a készlet betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async (transactionId: number) => {
    try {
      await inventoryApi.deleteFeedTransaction(transactionId);
      showSuccess('Tranzakció sikeresen törölve!');

      // Refresh both inventory and transactions
      loadInventory();
      loadTransactions();
    } catch (error: any) {
      console.error('Error deleting transaction:', error);
      handleApiError(error, 'Hiba a tranzakció törlésekor');
    }
  };

  const toLocalDateString = (dateValue: any): string => {
    if (!dateValue) return new Date().toISOString().split('T')[0];
    const d = new Date(dateValue);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const formatDate = (dateValue: any): string => {
    if (!dateValue) return '-';
    const d = new Date(dateValue);
    return d.toLocaleDateString('hu-HU');
  };

  const handleEditTransaction = (transaction: any) => {
    const txDate = toLocalDateString(transaction.transaction_date);
    setEditingTransaction({
      id: transaction.id,
      quantity: transaction.quantity || 0,
      quantity_unit: transaction.quantity_unit || 'kg',
      unit_cost: transaction.unit_cost ?? '',
      storage_location: transaction.storage_location || '',
      transaction_date: txDate,
      invoice_number: transaction.invoice_number || '',
      notes: transaction.notes || ''
    });
    setShowEditTransactionModal(true);
  };

  const handleUpdateTransaction = async () => {
    if (!editingTransaction) return;

    try {
      const payload = {
        quantity: parseFloat(editingTransaction.quantity),
        quantity_unit: editingTransaction.quantity_unit,
        unit_cost: editingTransaction.unit_cost ? parseFloat(editingTransaction.unit_cost) : null,
        storage_location: editingTransaction.storage_location,
        transaction_date: editingTransaction.transaction_date,
        invoice_number: editingTransaction.invoice_number || null,
        notes: editingTransaction.notes
      };

      await inventoryApi.updateFeedTransaction(editingTransaction.id, payload);
      showSuccess('Tranzakció sikeresen frissítve!');

      setShowEditTransactionModal(false);
      setEditingTransaction(null);

      // Refresh both inventory and transactions
      loadInventory();
      loadTransactions();
    } catch (error: any) {
      console.error('Error updating transaction:', error);
      handleApiError(error, 'Hiba a tranzakció frissítésekor');
    }
  };

  const handleDeleteMedicineTransaction = async (transactionId: number) => {
    try {
      await inventoryApi.deleteMedicineTransaction(transactionId);
      showSuccess('Gyógyszer tranzakció sikeresen törölve!');

      // Refresh both inventory and transactions
      loadInventory();
      loadMedicineTransactions();
    } catch (error: any) {
      console.error('Error deleting medicine transaction:', error);
      handleApiError(error, 'Hiba a gyógyszer tranzakció törlésekor');
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    const { action, id } = confirmAction;

    try {
      if (action === 'deleteFeed') await handleDeleteTransaction(id);
      else if (action === 'deleteMedicine') await handleDeleteMedicineTransaction(id);
    } finally {
      setConfirmAction(null);
    }
  };

  const handleEditMedicineTransaction = (transaction: any) => {
    const txDate = toLocalDateString(transaction.transaction_date);
    const expDate = transaction.expiry_date ? toLocalDateString(transaction.expiry_date) : '';
    setEditingMedicineTransaction({
      id: transaction.id,
      quantity: transaction.quantity || 0,
      quantity_unit: transaction.quantity_unit || 'ml',
      unit_cost: transaction.unit_cost ?? '',
      expiry_date: expDate,
      batch_number: transaction.batch_number || '',
      transaction_date: txDate,
      invoice_number: transaction.invoice_number || '',
      notes: transaction.notes || ''
    });
    setShowEditMedicineTransactionModal(true);
  };

  const handleUpdateMedicineTransaction = async () => {
    if (!editingMedicineTransaction) return;

    try {
      const payload = {
        quantity: parseFloat(editingMedicineTransaction.quantity),
        quantity_unit: editingMedicineTransaction.quantity_unit,
        unit_cost: editingMedicineTransaction.unit_cost ? parseFloat(editingMedicineTransaction.unit_cost) : null,
        expiry_date: editingMedicineTransaction.expiry_date,
        batch_number: editingMedicineTransaction.batch_number,
        transaction_date: editingMedicineTransaction.transaction_date,
        invoice_number: editingMedicineTransaction.invoice_number || null,
        notes: editingMedicineTransaction.notes
      };

      await inventoryApi.updateMedicineTransaction(editingMedicineTransaction.id, payload);
      showSuccess('Gyógyszer tranzakció sikeresen frissítve!');

      setShowEditMedicineTransactionModal(false);
      setEditingMedicineTransaction(null);

      // Refresh both inventory and transactions
      loadInventory();
      loadMedicineTransactions();
    } catch (error: any) {
      console.error('Error updating medicine transaction:', error);
      handleApiError(error, 'Hiba a gyógyszer tranzakció frissítésekor');
    }
  };

  const loadMasterData = async () => {
    try {
      const [feedsResponse, medicinesResponse, feedUnitsCategory, medicineUnitsCategory] = await Promise.all([
        fetch(`${API_BASE_URL}/feeds`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${API_BASE_URL}/medicines`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }),
        masterDataApi.getCategory('quantity_units'),
        masterDataApi.getCategory('medicine_quantity_units')
      ]);

      if (feedsResponse.ok) {
        const feeds = await feedsResponse.json();
        setAvailableFeeds(feeds);
      }

      if (medicinesResponse.ok) {
        const medicines = await medicinesResponse.json();
        setAvailableMedicines(medicines);
      }

      setFeedQuantityUnits(feedUnitsCategory.values || []);
      setMedicineQuantityUnits(medicineUnitsCategory.values || []);
    } catch (error) {
      console.error('Error loading master data:', error);
      handleApiError(error, 'Hiba a törzsadatok betöltésekor');
    }
  };

  const loadTransactions = async () => {
    if (!selectedFarmId) return;
    try {
      const transactionsResponse = await inventoryApi.getFeedTransactions(selectedFarmId);
      setTransactions(transactionsResponse);
    } catch (error) {
      console.error('Error loading transactions:', error);
      handleApiError(error, 'Hiba a tranzakciók betöltésekor');
    }
  };

  const loadMedicineTransactions = async () => {
    if (!selectedFarmId) return;
    try {
      const medicineTransactionsResponse = await inventoryApi.getMedicineTransactions(selectedFarmId);
      setMedicineTransactions(medicineTransactionsResponse);
    } catch (error) {
      console.error('Error loading medicine transactions:', error);
      handleApiError(error, 'Hiba a gyógyszer tranzakciók betöltésekor');
    }
  };

  const handleAddFeed = async () => {
    try {
      // Frontend validation
      if (!newFeed.feed_id) {
        showError('Kérlek válassz takarmányt!');
        return;
      }

      if (!newFeed.quantity || parseFloat(newFeed.quantity) <= 0) {
        showError('Kérlek adj meg egy érvényes mennyiséget!');
        return;
      }

      if (!newFeed.transaction_date) {
        showError('Kérlek add meg a beszerzés dátumát!');
        return;
      }
      
      const quantity = parseFloat(newFeed.quantity);
      const unitCost = newFeed.unit_cost ? parseFloat(newFeed.unit_cost) : null;
      
      const totalCost = newFeed.total_cost ? parseFloat(newFeed.total_cost) : (quantity * (unitCost || 0));

      const payload = {
        farm_id: selectedFarmId,
        feed_id: parseInt(newFeed.feed_id),
        quantity: quantity,
        quantity_unit: newFeed.quantity_unit || 'kg',
        unit_cost: unitCost,
        storage_location: newFeed.storage_location,
        transaction_date: newFeed.transaction_date,
        // Cost related fields
        invoice_number: newFeed.invoice_number,
        total_cost: totalCost,
        cost_notes: newFeed.cost_notes
      };
      
      await inventoryApi.addFeed(payload);
      
      setShowAddFeedModal(false);
      setNewFeed({
        feed_id: '',
        quantity: '',
        quantity_unit: '',
        unit_cost: '',
        storage_location: '',
        transaction_date: new Date().toISOString().split('T')[0],
        invoice_number: '',
        total_cost: '',
        cost_notes: ''
      });
      
      loadInventory();
      showSuccess('Takarmány sikeresen hozzáadva!');
    } catch (error: any) {
      console.error('Error adding feed:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      handleApiError(error, 'Hiba a takarmány hozzáadásakor');
    }
  };

  const handleAddMedicine = async () => {
    try {
      const quantity = parseFloat(newMedicine.quantity);
      const unitCost = newMedicine.unit_cost ? parseFloat(newMedicine.unit_cost) : 0;
      const totalCost = newMedicine.total_cost ? parseFloat(newMedicine.total_cost) : (quantity * unitCost);

      await inventoryApi.addMedicine({
        ...newMedicine,
        farm_id: selectedFarmId,
        quantity: quantity,
        unit_cost: unitCost,
        total_cost: totalCost
      });
      
      showSuccess('Gyógyszer sikeresen hozzáadva!');
      setShowAddMedicineModal(false);
      setNewMedicine({
        medicine_id: '',
        quantity: '',
        quantity_unit: '',
        expiry_date: '',
        batch_number: '',
        unit_cost: '',
        invoice_number: '',
        total_cost: ''
      });

      loadInventory();
    } catch (error) {
      console.error('Error adding medicine:', error);
      handleApiError(error, 'Hiba a gyógyszer hozzáadásakor');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Készletkezelés</h1>
            <p className="mt-2 text-gray-600">
              Takarmányok és gyógyszerek készletének nyomon követése
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="inventory-farm-select" className="text-sm font-medium text-gray-700">Telep:</label>
            <select
              id="inventory-farm-select"
              value={selectedFarmId || ''}
              onChange={(e) => setSelectedFarmId(parseInt(e.target.value))}
              className="rounded-md border-gray-300 shadow-sm focus-visible:border-green-500 focus-visible:ring-green-500 text-sm py-1.5 pl-3 pr-8"
            >
              {farms.map((farm) => (
                <option key={farm.id} value={farm.id}>{farm.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('inventory')}
            className={`${
              activeTab === 'inventory'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
          >
            Készlet
          </button>
          <button
            onClick={() => setActiveTab('transactions')}
            className={`${
              activeTab === 'transactions'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
          >
            Tranzakciók
          </button>
        </nav>
      </div>

      {activeTab === 'inventory' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Takarmányok */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Takarmányok ({feeds.length})
            </h3>
            <button
              onClick={() => setShowAddFeedModal(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
            >
              + Takarmány hozzáadása
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Név
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mennyiség
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Egységár
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Státusz
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {feeds.map((feed) => (
                  <tr key={feed.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {feed.feed?.name || 'Takarmány'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {feed.quantity} {feed.quantity_unit}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {feed.unit_cost ? `${feed.unit_cost} Ft/${feed.quantity_unit}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        feed.quantity !== undefined && feed.minimum_stock_alert && feed.quantity < feed.minimum_stock_alert ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {feed.quantity !== undefined && feed.minimum_stock_alert && feed.quantity < feed.minimum_stock_alert ? 'Alacsony' : 'Megfelelő'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Gyógyszerek */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Gyógyszerek ({medicines.length})
            </h3>
            <button
              onClick={() => setShowAddMedicineModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              + Gyógyszer hozzáadása
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Név
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mennyiség
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Egységár
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tétel szám
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lejárat
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {medicines.map((medicine) => (
                  <tr key={medicine.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {medicine.medicine?.name || 'Ismeretlen gyógyszer'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {medicine.quantity} {medicine.quantity_unit}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {medicine.unit_cost ? `${medicine.unit_cost} Ft/${medicine.quantity_unit}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {medicine.batch_number || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(medicine.expiry_date)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      )}

      {/* Takarmány hozzáadása modal */}
      {showAddFeedModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Takarmány hozzáadása</h3>
              <button
                onClick={() => setShowAddFeedModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Takarmány típusa</label>
                <select
                  value={newFeed.feed_id}
                  onChange={(e) => setNewFeed({...newFeed, feed_id: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                >
                  <option value="">Válassz takarmányt...</option>
                  {availableFeeds.map((feed) => (
                    <option key={feed.id} value={feed.id}>{feed.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Mennyiség</label>
                  <input
                    type="number"
                    value={newFeed.quantity}
                    onChange={(e) => setNewFeed({...newFeed, quantity: e.target.value})}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Egység</label>
                  <select
                    value={newFeed.quantity_unit}
                    onChange={(e) => setNewFeed({...newFeed, quantity_unit: e.target.value})}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  >
                    <option value="">Válassz egységet...</option>
                    {feedQuantityUnits.map((unit) => (
                      <option key={unit.key} value={unit.key}>{unit.value}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Egységár (Ft)</label>
                <input
                  type="number"
                  value={newFeed.unit_cost}
                  onChange={(e) => setNewFeed({...newFeed, unit_cost: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Tárolás helye</label>
                <input
                  type="text"
                  value={newFeed.storage_location}
                  onChange={(e) => setNewFeed({...newFeed, storage_location: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Beérkezés dátuma</label>
                <input
                  type="date"
                  value={newFeed.transaction_date}
                  onChange={(e) => setNewFeed({...newFeed, transaction_date: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Költség adatok</h4>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Számla szám</label>
                <input
                  type="text"
                  value={newFeed.invoice_number}
                  onChange={(e) => setNewFeed({...newFeed, invoice_number: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  placeholder="pl. SZ-2025-001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Teljes tételár (Ft)</label>
                <input
                  type="number"
                  value={newFeed.total_cost}
                  onChange={(e) => setNewFeed({...newFeed, total_cost: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  placeholder={`Számított: ${(parseFloat(newFeed.quantity || '0') * parseFloat(newFeed.unit_cost || '0')).toLocaleString('hu-HU')} Ft`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ha üresen hagyod, automatikusan kiszámítódik (mennyiség × egységár)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Megjegyzés</label>
                <textarea
                  value={newFeed.cost_notes}
                      onChange={(e) => setNewFeed({...newFeed, cost_notes: e.target.value})}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                      rows={2}
                      placeholder="További részletek a költségről..."
                    />
                  </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowAddFeedModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Mégse
              </button>
              <button
                onClick={handleAddFeed}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Hozzáadás
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gyógyszer hozzáadása modal */}
      {showAddMedicineModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Gyógyszer hozzáadása</h3>
              <button
                onClick={() => setShowAddMedicineModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Gyógyszer</label>
                <select
                  value={newMedicine.medicine_id}
                  onChange={(e) => setNewMedicine({...newMedicine, medicine_id: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Válassz gyógyszert...</option>
                  {availableMedicines.map((medicine) => (
                    <option key={medicine.id} value={medicine.id}>{medicine.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Mennyiség</label>
                  <input
                    type="number"
                    value={newMedicine.quantity}
                    onChange={(e) => setNewMedicine({...newMedicine, quantity: e.target.value})}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Egység</label>
                  <select
                    value={newMedicine.quantity_unit}
                    onChange={(e) => setNewMedicine({...newMedicine, quantity_unit: e.target.value})}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Válassz egységet...</option>
                    {medicineQuantityUnits.map((unit) => (
                      <option key={unit.key} value={unit.key}>{unit.value}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Lejárati dátum</label>
                <input
                  type="date"
                  value={newMedicine.expiry_date}
                  onChange={(e) => setNewMedicine({...newMedicine, expiry_date: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Tétel szám</label>
                <MasterDataCombobox
                  categoryName="medicine_batch_numbers"
                  value={newMedicine.batch_number}
                  onChange={(value) => setNewMedicine({...newMedicine, batch_number: value})}
                  allowCreate={true}
                  placeholder="Válassz vagy adj hozzá tételszámot..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Egységár (Ft) *</label>
                <input
                  type="number"
                  value={newMedicine.unit_cost}
                  onChange={(e) => setNewMedicine({...newMedicine, unit_cost: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Számla szám</label>
                <input
                  type="text"
                  value={newMedicine.invoice_number}
                  onChange={(e) => setNewMedicine({...newMedicine, invoice_number: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="pl. SZ-2025-001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Teljes tételár (Ft)</label>
                <input
                  type="number"
                  value={newMedicine.total_cost}
                  onChange={(e) => setNewMedicine({...newMedicine, total_cost: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder={`Számított: ${(parseFloat(newMedicine.quantity || '0') * parseFloat(newMedicine.unit_cost || '0')).toLocaleString('hu-HU')} Ft`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ha üresen hagyod, automatikusan kiszámítódik (mennyiség × egységár)
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowAddMedicineModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Mégse
              </button>
              <button
                onClick={handleAddMedicine}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Hozzáadás
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'transactions' && (
        <div className="space-y-8">
          {/* Takarmány Tranzakciók */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Takarmány Tranzakciók ({transactions.length})
              </h3>
            </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dátum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Takarmány
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Típus
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Célpont
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mennyiség
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Egységár
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tárolás
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Megjegyzés
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Műveletek
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(transaction.transaction_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {transaction.feed?.name || 'Ismeretlen takarmány'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        transaction.transaction_type === 'in' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {transaction.transaction_type === 'in' ? 'Bevét' : 'Kivét'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.target ? (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          transaction.target.type === 'individual' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                        }`}>
                          {transaction.target.label}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.display_quantity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.display_unit_cost || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.storage_location || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {transaction.reason} - {transaction.notes}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditTransaction(transaction)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Tranzakció szerkesztése"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => setConfirmAction({ action: 'deleteFeed', id: transaction.id })}
                          className="text-red-600 hover:text-red-900"
                          title="Tranzakció törlése"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Gyógyszer Tranzakciók */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Gyógyszer Tranzakciók ({medicineTransactions.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dátum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gyógyszer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Típus
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Célpont
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mennyiség
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Egységár
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lejárat
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tétel szám
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Megjegyzés
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Műveletek
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {medicineTransactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(transaction.transaction_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {transaction.medicine?.name || 'Ismeretlen gyógyszer'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        transaction.transaction_type === 'in' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {transaction.transaction_type === 'in' ? 'Bevét' : 'Kivét'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.target ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {transaction.target.label}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.display_quantity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.display_unit_cost || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(transaction.expiry_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.batch_number || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {transaction.reason} - {transaction.notes}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditMedicineTransaction(transaction)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Tranzakció szerkesztése"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => setConfirmAction({ action: 'deleteMedicine', id: transaction.id })}
                          className="text-red-600 hover:text-red-900"
                          title="Tranzakció törlése"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      )}

      {/* Edit Transaction Modal */}
      {showEditTransactionModal && editingTransaction && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Tranzakció szerkesztése
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Mennyiség</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingTransaction.quantity}
                    onChange={(e) => setEditingTransaction({...editingTransaction, quantity: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Egység</label>
                  <input
                    type="text"
                    value={editingTransaction.quantity_unit}
                    disabled
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Egységár</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingTransaction.unit_cost}
                    onChange={(e) => setEditingTransaction({...editingTransaction, unit_cost: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Tárolás helye</label>
                  <input
                    type="text"
                    value={editingTransaction.storage_location}
                    onChange={(e) => setEditingTransaction({...editingTransaction, storage_location: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Dátum</label>
                  <input
                    type="date"
                    value={editingTransaction.transaction_date}
                    onChange={(e) => setEditingTransaction({...editingTransaction, transaction_date: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Számla szám</label>
                  <input
                    type="text"
                    value={editingTransaction.invoice_number}
                    onChange={(e) => setEditingTransaction({...editingTransaction, invoice_number: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="pl. SZ-2025-001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Megjegyzés</label>
                  <input
                    type="text"
                    value={editingTransaction.notes}
                    onChange={(e) => setEditingTransaction({...editingTransaction, notes: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleUpdateTransaction}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Frissítés
                </button>
                <button
                  onClick={() => {
                    setShowEditTransactionModal(false);
                    setEditingTransaction(null);
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Mégse
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Medicine Transaction Modal */}
      {showEditMedicineTransactionModal && editingMedicineTransaction && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Gyógyszer tranzakció szerkesztése</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Mennyiség</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingMedicineTransaction.quantity}
                    onChange={(e) => setEditingMedicineTransaction({...editingMedicineTransaction, quantity: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Mértékegység</label>
                  <input
                    type="text"
                    value={editingMedicineTransaction.quantity_unit}
                    disabled
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Egységár</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingMedicineTransaction.unit_cost}
                    onChange={(e) => setEditingMedicineTransaction({...editingMedicineTransaction, unit_cost: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Lejárati dátum</label>
                  <input
                    type="date"
                    value={editingMedicineTransaction.expiry_date}
                    disabled
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Tétel szám</label>
                  <input
                    type="text"
                    value={editingMedicineTransaction.batch_number}
                    disabled
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Dátum</label>
                  <input
                    type="date"
                    value={editingMedicineTransaction.transaction_date}
                    onChange={(e) => setEditingMedicineTransaction({...editingMedicineTransaction, transaction_date: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Számla szám</label>
                  <input
                    type="text"
                    value={editingMedicineTransaction.invoice_number}
                    onChange={(e) => setEditingMedicineTransaction({...editingMedicineTransaction, invoice_number: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="pl. SZ-2025-001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Megjegyzés</label>
                  <input
                    type="text"
                    value={editingMedicineTransaction.notes}
                    onChange={(e) => setEditingMedicineTransaction({...editingMedicineTransaction, notes: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleUpdateMedicineTransaction}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Frissítés
                </button>
                <button
                  onClick={() => {
                    setShowEditMedicineTransactionModal(false);
                    setEditingMedicineTransaction(null);
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Mégse
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmAction !== null}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmAction(null)}
        title="Tranzakció törlése"
        message={confirmAction?.action === 'deleteFeed'
          ? 'Biztosan törölni szeretnéd ezt a tranzakciót?'
          : 'Biztosan törölni szeretnéd ezt a gyógyszer tranzakciót?'
        }
        confirmLabel="Törlés"
      />
    </div>
  );
};

export default Inventory;